import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery, withPrefix } from "gatsby";
import SiteModal from "../modal";
import useIsMobile from "../../hooks/use-is-mobile";

const CompareContext = createContext({
  productIds: [],
  toggleProduct: () => undefined,
  removeProduct: () => undefined,
  clearProducts: () => undefined,
});

export const CompareProvider = ({ category, products, children }) => {
  const [productIds, setProductIds] = useState([]);
  const [pageSlug, setPageSlug] = useState();
  const [productId, setProductId] = useState();
  const [loaded, setLoaded] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const isMobile = useIsMobile(1023);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const closeInfoModal = useCallback(() => setInfoModalVisible(false), []);

  useEffect(() => {
    if (productIds.length && products) {
      const product = products?.find((prod) => prod.key_prod === productIds[0]);
      setPageSlug(product.pageLine.slug);
    } else {
      setPageSlug();
    }
  }, [productIds, products]);

  const toggleProduct = useCallback(
    (id) =>
      setProductIds((list) => {
        if (list.includes(id)) {
          return list.filter((item) => item !== id);
        } else {
          const product = products?.find((prod) => prod.key_prod === id);
          if (product?.pageLine?.slug === pageSlug || !pageSlug) {
            if (isMobile ? list.length < 2 : list.length < 4) {
              return list.concat(id);
            } else {
              setWarningModalVisible(true);
              return list;
            }
          } else {
            setProductId(id);
            setInfoModalVisible(true);
            return list;
          }
        }
      }),
    [isMobile, pageSlug, products]
  );
  useEffect(() => {
    const storageData = localStorage.getItem(withPrefix(`_comp-${category}`).substring(1));
    if (storageData) {
      const { productIds = [] } = JSON.parse(storageData);
      setProductIds(productIds);
    }
    setLoaded(true);
  }, [category]);
  const removeProduct = useCallback(
    (id) => setProductIds((list) => list.filter((item) => item !== id)),
    []
  );
  const clearProducts = useCallback(() => setProductIds([]), []);

  const updateCompare = useCallback(() => setProductIds([productId]), [productId]);

  useEffect(() => {
    if (isMobile && productIds.length > 2) {
      setProductIds((current) => current.slice(0, 2));
    }
  }, [isMobile, productIds.length]);

  useEffect(() => {
    const storageKey = withPrefix(`_comp-${category}`).substring(1);
    if (productIds.length) {
      localStorage.setItem(storageKey, JSON.stringify({ productIds }));
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [productIds, category]);

  const { labelManagerLabels } = useStaticQuery(graphql`
    {
      labelManagerLabels: labelManagerLabels {
        youCantCompareMoreThan
        attention
        close
        products
        changeCompare
      }
    }
  `);

  const providerValue = useMemo(
    () => ({ productIds, toggleProduct, removeProduct, clearProducts, loaded }),
    [productIds, toggleProduct, removeProduct, clearProducts, loaded]
  );

  return (
    <CompareContext.Provider value={providerValue}>
      {children}
      <SiteModal
        show={warningModalVisible}
        onHide={() => setWarningModalVisible(false)}
        className="compare-modal"
      >
        <h1>{labelManagerLabels.attention}</h1>
        {`${labelManagerLabels.youCantCompareMoreThan} ${
          isMobile ? 2 : 4
        } ${labelManagerLabels.products.toLowerCase()}.`}
      </SiteModal>
      <SiteModal
        show={infoModalVisible}
        onHide={closeInfoModal}
        isChangeCompare={true}
        updateCompare={updateCompare}
        className="compare-modal"
      >
        <h1>{labelManagerLabels.attention}</h1>
        {labelManagerLabels.changeCompare}
      </SiteModal>
    </CompareContext.Provider>
  );
};

CompareProvider.propTypes = {
  category: PropTypes.string.isRequired,
};

export default CompareContext;
